import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Outlet, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { ToastProvider } from './context/ToastContext';
import './index.css';

// ─── Lazy-loaded pages ────────────────────────────────────────────────────────
const LandingPage        = lazy(() => import('./pages/LandingPage'));
const PricingPage        = lazy(() => import('./pages/PricingPage'));
const TrailsLibraryPage  = lazy(() => import('./pages/TrailsLibraryPage'));
const AuthPage           = lazy(() => import('./pages/AuthPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage  = lazy(() => import('./pages/ResetPasswordPage'));
const Dashboard          = lazy(() => import('./pages/Dashboard'));
const TopicView          = lazy(() => import('./pages/TopicView'));
const QuizView           = lazy(() => import('./pages/QuizView'));
const Onboarding         = lazy(() => import('./pages/Onboarding'));
const UploadView         = lazy(() => import('./pages/UploadView'));
const ProgressView       = lazy(() => import('./pages/ProgressView'));
const SettingsView       = lazy(() => import('./pages/SettingsView'));
const CheatSheetView     = lazy(() => import('./pages/CheatSheetView'));
const FlashcardView      = lazy(() => import('./pages/FlashcardView'));
const TeachMeView        = lazy(() => import('./pages/TeachMeView'));
const ExamSimulatorView  = lazy(() => import('./pages/ExamSimulatorView'));
const ExamReportView     = lazy(() => import('./pages/ExamReportView'));
const ContactPage        = lazy(() => import('./pages/ContactPage'));
const AboutPage          = lazy(() => import('./pages/AboutPage'));
const FaqPage            = lazy(() => import('./pages/FaqPage'));
const WelcomePage        = lazy(() => import('./pages/WelcomePage'));
const AdminDashboard     = lazy(() => import('./pages/AdminDashboard'));
const InviteJoinPage     = lazy(() => import('./pages/InviteJoinPage'));
const GroupTrailPage     = lazy(() => import('./pages/GroupTrailPage'));
const NotFoundPage       = lazy(() => import('./pages/NotFoundPage'));
const Termspage          = lazy(() => import('./pages/Termspage'));
const Privacypage        = lazy(() => import('./pages/Privacypage'));
const Refundpage         = lazy(() => import('./pages/Refundpage'));
const ServicePolicyPage  = lazy(() => import('./pages/ServicePolicyPage'));
const CheckoutSuccessPage = lazy(() => import('./pages/CheckoutSuccessPage'));

// New SEO/Traffic pages
const PdfToNotesPage      = lazy(() => import('./pages/tools/PdfToNotesPage'));
const PdfToFlashcardsPage = lazy(() => import('./pages/tools/PdfToFlashcardsPage'));
const PdfToQuizPage       = lazy(() => import('./pages/tools/PdfToQuizPage'));
const PdfToVideoPage      = lazy(() => import('./pages/tools/PdfToVideoPage'));
const PdfToStudyGuidePage = lazy(() => import('./pages/tools/PdfToStudyGuidePage'));
const MissionPage         = lazy(() => import('./pages/MissionPage'));
const PrinciplesPage      = lazy(() => import('./pages/PrinciplesPage'));
const PressPage           = lazy(() => import('./pages/PressPage'));
const ResearchPage        = lazy(() => import('./pages/ResearchPage'));
const BlogIndexPage       = lazy(() => import('./pages/blog/BlogIndexPage'));
const BlogPostPage        = lazy(() => import('./pages/blog/BlogPostPage'));

// Capability Pages
// Public Labs SEO/AEO Pages
const LabsHubPublicPage           = lazy(() => import('./pages/labs/LabsHubPublicPage'));
const MathMistakeFinderPage       = lazy(() => import('./pages/labs/MathMistakeFinderPage'));
const CodeDebuggingTutorPage      = lazy(() => import('./pages/labs/CodeDebuggingTutorPage'));
const BiologyPathwaySimulatorPage = lazy(() => import('./pages/labs/BiologyPathwaySimulatorPage'));

const SocraticTutorPage   = lazy(() => import('./pages/features/SocraticTutorPage'));
const ExamSimulatorPage   = lazy(() => import('./pages/features/ExamSimulatorPage'));
const ExamReadinessPage   = lazy(() => import('./pages/features/ExamReadinessPage'));
const StudyPlannerPage    = lazy(() => import('./pages/features/StudyPlannerPage'));
const MathLabPage         = lazy(() => import('./pages/MathLabPage'));
const CodeLabPage         = lazy(() => import('./pages/CodeLabPage'));
const BioSystemsLabPage   = lazy(() => import('./pages/BioSystemsLabPage'));
const LabsHubPage         = lazy(() => import('./pages/LabsHubPage'));

// Vs Pages
const VsQuizletPage       = lazy(() => import('./pages/vs/VsQuizletPage'));
const VsAnkiPage          = lazy(() => import('./pages/vs/VsAnkiPage'));
const VsStudyfetchPage    = lazy(() => import('./pages/vs/VsStudyfetchPage'));
const VsChatgptPage       = lazy(() => import('./pages/vs/VsChatgptPage'));

// Non-page components stay eagerly loaded
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import FeedbackButton from './components/FeedbackButton';
import TommyCompanion from './components/TommyCompanion';
import MaintenanceBanner from './components/MaintenanceBanner';
import PageTransition from './components/PageTransition';
import { useTheme } from './context/ThemeContext';
import { useEffect } from 'react';

// ─── Theme Applier ────────────────────────────────────────────────────────
function ThemeApplier() {
  const { theme } = useTheme();
  const location = useLocation();

  useEffect(() => {
    const root = window.document.documentElement;
    const isAppRoute = location.pathname.startsWith('/app');

    // Remove old classes and attributes
    root.classList.remove('light', 'dark');
    root.removeAttribute('data-theme');

    // If we're not in the app, always use light mode (so the marketing pages aren't affected)
    if (!isAppRoute) {
      root.classList.add('light');
      root.setAttribute('data-theme', 'light');
      return;
    }

    // Otherwise, apply the selected theme
    let activeTheme = theme;
    if (theme === 'system') {
      activeTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }

    root.classList.add(activeTheme);
    root.setAttribute('data-theme', activeTheme);
  }, [theme, location.pathname]);

  // Listen for system theme changes if mode is 'system'
  useEffect(() => {
    if (theme !== 'system' || !location.pathname.startsWith('/app')) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      const root = window.document.documentElement;
      root.classList.remove('light', 'dark');
      const systemTheme = mediaQuery.matches ? 'dark' : 'light';
      root.classList.add(systemTheme);
      root.setAttribute('data-theme', systemTheme);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme, location.pathname]);

  return null;
}

// ─── Suspense fallback ────────────────────────────────────────────────────────
function PageLoader() {
  return (
    <div className="h-screen w-full flex items-center justify-center bg-[#FBF7F1]">
      <div className="w-8 h-8 rounded-full border-4 border-[#EBE5DE] border-t-[#ff795c] animate-spin" />
    </div>
  );
}

function TransitionLayout() {
  return (
    <PageTransition>
      <Suspense fallback={<PageLoader />}>
        <Outlet />
      </Suspense>
    </PageTransition>
  );
}

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <>
      <ThemeApplier />
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
        <Route element={<TransitionLayout />}>
          {/* Public */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/trails" element={<TrailsLibraryPage />} />
          <Route path="/refund-policy" element={<Refundpage />} />
          <Route path="/privacy-policy" element={<Privacypage />} />
          <Route path="/terms-of-service" element={<Termspage />} />
          <Route path="/Privacy-policy" element={<Navigate to="/privacy-policy" replace />} />
          <Route path="/Terms-of-Service" element={<Navigate to="/terms-of-service" replace />} />
          <Route path="/service-policy" element={<ServicePolicyPage />} />
          <Route path="/login" element={<AuthPage defaultMode="signin" />} />
          <Route path="/signup" element={<AuthPage defaultMode="signup" />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/faq" element={<FaqPage />} />
          
          {/* SEO/Traffic Public Pages */}
          <Route path="/pdf-to-notes" element={<PdfToNotesPage />} />
          <Route path="/pdf-to-flashcards" element={<PdfToFlashcardsPage />} />
          <Route path="/pdf-to-quiz" element={<PdfToQuizPage />} />
          <Route path="/pdf-to-video" element={<PdfToVideoPage />} />
          <Route path="/pdf-to-study-guide" element={<PdfToStudyGuidePage />} />
          <Route path="/mission" element={<MissionPage />} />
          <Route path="/principles" element={<PrinciplesPage />} />
          <Route path="/press" element={<PressPage />} />
          <Route path="/research" element={<ResearchPage />} />
          <Route path="/blog" element={<BlogIndexPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/vs/quizlet" element={<VsQuizletPage />} />
          <Route path="/vs/anki" element={<VsAnkiPage />} />
          <Route path="/vs/studyfetch" element={<VsStudyfetchPage />} />
          <Route path="/vs/chatgpt" element={<VsChatgptPage />} />

          {/* Capabilities */}
          <Route path="/socratic-tutor" element={<SocraticTutorPage />} />
          <Route path="/exam-simulator" element={<ExamSimulatorPage />} />
          <Route path="/exam-readiness" element={<ExamReadinessPage />} />
          <Route path="/study-planner" element={<StudyPlannerPage />} />
          {/* Public Labs */}
          <Route path="/labs" element={<LabsHubPublicPage />} />
          <Route path="/math-mistake-finder" element={<MathMistakeFinderPage />} />
          <Route path="/code-debugging-tutor" element={<CodeDebuggingTutorPage />} />
          <Route path="/biology-pathway-simulator" element={<BiologyPathwaySimulatorPage />} />

          <Route path="/welcome" element={<ProtectedRoute><WelcomePage /></ProtectedRoute>} />
          <Route path="/invite/:inviteCode" element={<InviteJoinPage />} />
    
          {/* Protected — all /app/* routes require a session */}
          <Route path="/app/onboarding"                        element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
          <Route path="/app/dashboard"                         element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/app/subjects/:subjectId"               element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/app/subjects/:subjectId/cheat-sheet"   element={<ProtectedRoute><CheatSheetView /></ProtectedRoute>} />
          <Route path="/app/topic/:topicId"                    element={<ProtectedRoute><TopicView /></ProtectedRoute>} />
          <Route path="/app/topic/:topicId/teach-me"           element={<ProtectedRoute><TeachMeView /></ProtectedRoute>} />
          <Route path="/app/labs"                              element={<ProtectedRoute><LabsHubPage /></ProtectedRoute>} />
          <Route path="/app/topic/:topicId/math-lab"           element={<ProtectedRoute><MathLabPage /></ProtectedRoute>} />
          <Route path="/app/math-lab"                          element={<ProtectedRoute><MathLabPage /></ProtectedRoute>} />
          <Route path="/app/topic/:topicId/code-lab"           element={<ProtectedRoute><CodeLabPage /></ProtectedRoute>} />
          <Route path="/app/code-lab"                          element={<ProtectedRoute><CodeLabPage /></ProtectedRoute>} />
          <Route path="/app/topic/:topicId/bio-lab"            element={<ProtectedRoute><BioSystemsLabPage /></ProtectedRoute>} />
          <Route path="/app/bio-lab"                           element={<ProtectedRoute><BioSystemsLabPage /></ProtectedRoute>} />
          <Route path="/app/topic/:topicId/flashcards"         element={<ProtectedRoute><FlashcardView /></ProtectedRoute>} />
          <Route path="/app/quiz/:quizId"                      element={<ProtectedRoute><QuizView /></ProtectedRoute>} />
          <Route path="/app/upload"                            element={<ProtectedRoute><UploadView /></ProtectedRoute>} />
          <Route path="/app/progress"                          element={<ProtectedRoute><ProgressView /></ProtectedRoute>} />
          <Route path="/app/settings"                          element={<ProtectedRoute><SettingsView /></ProtectedRoute>} />
          <Route path="/app/groups/:groupId/trail"             element={<ProtectedRoute><GroupTrailPage /></ProtectedRoute>} />
          <Route path="/app/exam/:subjectId"                  element={<ProtectedRoute><ExamSimulatorView /></ProtectedRoute>} />
          <Route path="/app/exam-report/:attemptId"           element={<ProtectedRoute><ExamReportView /></ProtectedRoute>} />

          {/* Admin */}
          <Route path="/app/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />

          {/* Polar checkout success redirect */}
          <Route path="/app/checkout/success" element={<ProtectedRoute><CheckoutSuccessPage /></ProtectedRoute>} />

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </AnimatePresence>
    </>
  );
}

function GlobalTommyCompanion() {
  const { pathname } = useLocation();
  // Don't show companion on distraction-free exam simulator
  if (pathname.includes('/app/exam/')) return null;

  // Only show on authenticated /app/* views
  if (!pathname.startsWith('/app')) return null;

  const context = pathname.includes('/quiz/')
    ? 'quiz'
    : pathname.includes('/topic/') || pathname.includes('/flashcards')
      ? 'study'
      : pathname.startsWith('/app')
        ? 'dashboard'
        : 'general';

  return <TommyCompanion currentContext={context} />;
}

function App() {
  return (
    <ToastProvider>
      <Router>
        <AnimatedRoutes />
        {/* Global feedback button — visible on all authenticated /app/* pages */}
        <FeedbackButton />
        {/* Interactive Tommy Study Buddy — keeps users engaged across learning sessions */}
        <GlobalTommyCompanion />
      </Router>
      {/* Maintenance overlay — sits above everything, polls /health every 30s */}
      <MaintenanceBanner />
    </ToastProvider>
  );
}

export default App;
